/**
 * Self-exclusion (boxed's `selfExcludedUntil`): a shopper blocks their own spending for a number
 * of days they choose. Until it ends they may not open, enter a battle, buy into a live break or
 * buy balance; everything that settles what they already have (claim, sell-back, withdrawal) and
 * everything that spends nothing carries on. The end only ever moves later, and nothing lifts it
 * early, staff included. The rule, and what a shopper is told about it: platform words, the same
 * on every store apart from its terms and currency, so never a store's copy.
 */
import type { StorefrontTerminology } from "./storefrontTerminology.js";

/** The lengths a shopper may choose, in days (boxed's presets). */
export const SELF_EXCLUSION_DAY_OPTIONS = [1, 3, 7, 14, 30] as const;
export type SelfExclusionDays = (typeof SELF_EXCLUSION_DAY_OPTIONS)[number];

/** Whether an exclusion ending at `until` (the wire's ISO string, or null) still holds at `now`. */
export function isSelfExcludedAt(until: string | Date | null, now: Date): boolean {
  if (until === null) return false;
  const end = until instanceof Date ? until.getTime() : Date.parse(until);
  return Number.isFinite(end) && end > now.getTime();
}

/** What the store offers, which decides what an exclusion blocks and leaves open. */
export interface SelfExclusionWordsInput {
  terms: StorefrontTerminology;
  /** The currency's plural name ("credits"). */
  currencyPlural: string;
  features: {
    balancePurchases: boolean;
    battles: boolean;
    chat: boolean;
    forge: boolean;
    liveBreaks: boolean;
  };
}

/** A refused open, battle entry or purchase, given the exclusion's end formatted (null: unknown). */
export function selfExcludedRefusal(until: string | null): string {
  return until === null
    ? "Your account is self-excluded."
    : `Your account is self-excluded until ${until}.`;
}

/** "1 day", "7 days". */
export function selfExclusionDurationLabel(days: number): string {
  return `${days} day${days === 1 ? "" : "s"}`;
}

/**
 * Time left until `until`, from `now`: "6d 04h 12m", "04h 12m" under a day; null once it has
 * ended.
 */
export function selfExclusionTimeLeft(until: Date, now: Date): string | null {
  const ms = until.getTime() - now.getTime();
  if (!(ms > 0)) return null;
  const minutes = Math.floor(ms / 60_000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${days > 0 ? `${days}d ` : ""}${pad(hours)}h ${pad(minutes % 60)}m`;
}

/**
 * Every line of the self-exclusion panel and its dialog. `until` is the end already formatted
 * for the shopper ("November 10, 2026 at 3:04 PM PST").
 */
export function selfExclusionWords({ terms, currencyPlural, features }: SelfExclusionWordsInput) {
  const boxes = terms.box.plural;
  const battles = terms.battle.plural;
  const blocked = [
    `Open ${boxes}`,
    ...(features.battles ? [`Create or join ${battles}`] : []),
    ...(features.liveBreaks ? ["Buy into live breaks"] : []),
    ...(features.balancePurchases ? [`Buy ${currencyPlural}`] : []),
  ];
  const allowed = [
    `Claim or sell back ${terms.item.plural} you have already revealed`,
    `Sell ${terms.item.plural} from your ${terms.inventory.singular}`,
    ...(features.chat ? ["Chat with other players"] : []),
    ...(features.forge ? [`Build ${boxes} in the ${terms.forge.singular}`] : []),
  ];
  const spending = features.balancePurchases
    ? `buy ${currencyPlural} or spend them`
    : `spend ${currencyPlural}`;
  return {
    title: "Self-exclusion",
    description: `Take a break: block opening ${boxes}${features.battles ? `, ${battles}` : ""} and other spending on your account for as long as you choose.`,
    excludedTitle: "Account self-excluded",
    excludedDescription: (until: string) =>
      `Your account is self-excluded until ${until}. Access comes back on its own after that.`,
    /** The one thing to know before starting. */
    irreversible:
      "Once it starts, a self-exclusion cannot be shortened or lifted, not even by contacting support.",
    /** One line for the panel while one is in force. */
    blockedSummary: `Opening ${boxes}${features.battles ? `, ${battles}` : ""} and other spending are blocked.`,
    blockedTitle: "While self-excluded, you cannot:",
    blocked,
    allowedTitle: "While self-excluded, you can still:",
    allowed,
    start: "Self-exclude",
    details: "Details",
    timeLeft: (left: string) => `${left} left`,
    dialog: {
      title: "Self-exclusion",
      body: `You will not be able to ${spending} on ${boxes}${features.battles ? ` or ${battles}` : ""} for the time you choose.`,
      durationLabel: "How long",
      durationPlaceholder: "Choose how long",
      cancel: "Cancel",
      continue: "Continue",
      confirmTitle: "Confirm self-exclusion",
      confirmBody: (until: string) =>
        `Your account will not be able to ${spending} until ${until}.`,
      confirm: "Self-exclude",
      dismiss: "Close",
    },
    started: (until: string) => `You are self-excluded until ${until}.`,
    startFailed: "Could not start the self-exclusion. Try again.",
    refused: selfExcludedRefusal,
  };
}
export type SelfExclusionWords = ReturnType<typeof selfExclusionWords>;
